import React, { useState, useEffect } from "react";

const Engage = () => {
  const [activeTab, setActiveTab] = useState("invest");
  const [animatedValue, setAnimatedValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const interval = setInterval(() => {
      setAnimatedValue((prev) => {
        if (prev >= 97) {
          clearInterval(interval);
          return 97;
        }
        return prev + 1;
      });
    }, 30);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-12 tracking-wide">
      {/* Header Section */}
      <div
        className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-primaryBgColor mb-4">
          Grow Your Wealth with{" "}
          <span className="text-primaryBtnHoverColor">Sustainable Farming</span>
        </h1>
        <p className="text-xl text-secondaryTextColor max-w-3xl mx-auto">
          Invest in sustainable agriculture and earn competitive returns while
          supporting local communities and the environment.
        </p>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
        {[
          { value: `${animatedValue}%`, label: "Average Annual Return" },
          { value: "500+", label: "Active Investors" },
          { value: "$12M+", label: "Total Investments" },
          { value: "24", label: "Farm Locations" },
        ].map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl p-6 text-center shadow-lg"
          >
            <div className="text-3xl font-bold text-primaryBtnColor mb-2">
              {item.value}
            </div>
            <div className="text-secondaryTextColor">{item.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-16">
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          {[
            { key: "invest", label: "Investment Opportunities" },
            { key: "impact", label: "Our Impact" },
            { key: "process", label: "How It Works" },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`flex-1 py-4 px-6 text-lg font-medium ${
                activeTab === tab.key
                  ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
                  : "text-secondaryTextColor"
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {activeTab === "invest" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Short-Term Crops",
                  desc: "6-12 month investment in seasonal crops with quick returns.",
                  roi: "8-12% ROI",
                },
                {
                  title: "Orchard Development",
                  desc: "2-5 year investment in fruit trees with substantial returns.",
                  roi: "15-22% ROI",
                },
                {
                  title: "Livestock Program",
                  desc: "1-3 year investment in sustainable livestock farming.",
                  roi: "10-18% ROI",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="bg-[#FFF8F6] rounded-xl p-6 border border-gray-200 transition-all duration-300 hover:shadow-lg"
                >
                  <h3 className="text-xl font-semibold text-primaryTextColor mb-2">
                    {card.title}
                  </h3>
                  <p className="text-secondaryTextColor mb-4">{card.desc}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-primaryBtnColor font-bold">
                      {card.roi}
                    </span>
                    {/* <button className="bg-primaryBtnColor text-white px-4 py-2 rounded-lg text-sm hover:bg-primaryBtnHoverColor transition-colors">
                        Invest Now
                      </button> */}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "impact" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-primaryTextColor mb-6">
                  Our Environmental Impact
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      title: "Sustainable Practices",
                      desc: "All our farms use organic methods, conserving water and protecting soil health.",
                    },
                    {
                      title: "Carbon Negative",
                      desc: "Our operations sequester 2x more carbon than we emit, fighting climate change.",
                    },
                    {
                      title: "Community Development",
                      desc: "We've created 350+ jobs in rural communities, providing stable incomes.",
                    },
                  ].map((impact, i) => (
                    <div key={i} className="flex items-start">
                      <div className="bg-primaryBtnColor p-2 rounded-lg mr-4 text-white">
                        ✓
                      </div>
                      <div>
                        <h4 className="font-semibold text-primaryTextColor">
                          {impact.title}
                        </h4>
                        <p className="text-secondaryTextColor">{impact.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="bg-primaryBgColor text-white rounded-xl p-6 h-full">
                  <h3 className="text-2xl font-semibold mb-6">
                    Impact Metrics
                  </h3>
                  {[
                    {
                      label: "Carbon Sequestered",
                      value: "12,450 tons",
                      width: "w-4/5",
                    },
                    {
                      label: "Organic Farmland",
                      value: "1,250 acres",
                      width: "w-3/4",
                    },
                    { label: "Jobs Created", value: "350+", width: "w-[90%]" },
                  ].map((metric, i) => (
                    <div key={i} className="mb-6">
                      <div className="flex justify-between mb-2">
                        <span>{metric.label}</span>
                        <span className="font-bold">{metric.value}</span>
                      </div>
                      <div className="w-full bg-gray-600 h-2 rounded-full">
                        <div
                          className={`bg-primaryBtnHoverColor h-2 rounded-full ${metric.width}`}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "process" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-semibold text-primaryTextColor mb-6">
                  Simple Investment Process
                </h3>
                <div className="space-y-6">
                  {[
                    "Select Investment",
                    "Complete Investment",
                    "Track Growth",
                    "Receive Returns",
                  ].map((step, i) => (
                    <div key={i} className="flex">
                      <div className="bg-primaryBtnColor w-10 h-10 rounded-full flex items-center justify-center mr-4 text-white font-bold">
                        {i + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-primaryTextColor">
                          {step}
                        </h4>
                        <p className="text-secondaryTextColor">
                          {i === 0
                            ? "Choose from our vetted farming opportunities based on your goals."
                            : i === 1
                            ? "Invest securely online with as little as $500."
                            : i === 2
                            ? "Monitor your investment through our dashboard with regular updates."
                            : "Get quarterly distributions and annual returns on your investment."}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="bg-[#FFF8F6] rounded-xl p-8 border border-gray-200">
                  <h4 className="text-xl font-semibold text-primaryTextColor mb-4">
                    Ready to Invest?
                  </h4>
                  <p className="text-secondaryTextColor mb-6">
                    Join hundreds of investors growing their wealth through
                    sustainable agriculture.
                  </p>
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-primaryTextColor mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryBtnHoverColor focus:border-transparent"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-3 px-4 rounded-lg font-semibold hover:scale-105 transition duration-300 transform shadow-lg"
                    >
                      Get Started Today
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Testimonial Section */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-4">
        <h2 className="text-3xl font-bold text-center text-primaryBgColor mb-12">
          What Our Investors Say
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              name: "Michael Johnson",
              since: "Investor since 2020",
              quote:
                "I've earned consistent 12% returns while knowing my money is supporting sustainable agriculture.",
            },
            {
              name: "Sarah Williams",
              since: "Investor since 2021",
              quote:
                "The transparency and regular updates make me confident about where my money is going.",
            },
            {
              name: "David Chen",
              since: "Investor since 2019",
              quote:
                "Finally an investment that aligns with my values and delivers excellent financial returns.",
            },
          ].map((testimonial, i) => (
            <div key={i} className="text-center">
              <div className="bg-primaryBtnColor w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-lg font-bold">
                “
              </div>
              <p className="text-secondaryTextColor italic mb-4">
                "{testimonial.quote}"
              </p>
              <div>
                <div className="font-semibold text-primaryTextColor">
                  {testimonial.name}
                </div>
                <div className="text-sm text-secondaryTextColor">
                  {testimonial.since}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Engage;
